import { action, computed, observable, makeAutoObservable } from 'mobx';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { AppState } from 'src/AppState/AppState';
import { arrayMove } from 'react-movable';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { TypeId } from 'src_common/common/websocket2/type';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { getMarketStatus } from 'src/domains/sportsbook/trading/view/eventDetails/eventResults/EventResults.state';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { PossibleMarketsType } from 'src/AppState/sdk/staff';

export type MarketForRenderType = {
    displayOrder: number;
    id: number;
    expanded: boolean;
    templateId: string;
    items: Array<{ market: EventMarketItemType; expanded: boolean; groupName: string }>;
};

export function reorderMarkets(markets: Array<MarketForRenderType>): Record<number, { displayOrder: number }> {
    const sortedMarkets: Record<number, { displayOrder: number }> = {};
    let index = 0;
    for (const marketGroup of markets) {
        for (const item of marketGroup.items) {
            index++;
            sortedMarkets[TypeId.getMarketId(item.market.id)] = { displayOrder: index };
        }
    }

    return sortedMarkets;
}

const AVAILABLE_MARKET_STATES = [
    {
        id: 'open',
        label: 'Open',
    },
    {
        id: 'new',
        label: 'New',
    },
    {
        id: 'resulted',
        label: 'Resulted',
    },
    {
        id: 'settled',
        label: 'Settled',
    },
    {
        id: 'suspended',
        label: 'Suspended',
    },
];

export type GroupMarketsType = Record<string, MarketForRenderType>;

interface AvailableItemType {
    id: string;
    name: string;
}

const MarketStateIO = t.union([
    t.literal('open'),
    t.literal('new'),
    t.literal('resulted'),
    t.literal('settled'),
    t.literal('suspended'),
]);
type MarketStateType = t.TypeOf<typeof MarketStateIO>;
const isMarketStateType = createGuard(MarketStateIO);

export class EventMarketsState {
    @observable public add: boolean = false;
    @observable public filter: MarketStateType = 'open';
    @observable public openedMarket: number | null = null;
    @observable private opened: string | null = null;
    @observable public isConfirmPopupOpened: boolean = false;
    @observable private readonly possibleMarkets: Resource<PossibleMarketsType>;

    public constructor(
        private readonly eventId: EventId,
        private readonly appState: AppState
    ) {
        this.possibleMarkets = new Resource(async (): Promise<PossibleMarketsType> => {
            return await this.appState.sdkStaff.staff_event_possible_markets({
                event_id: this.eventId.toOldId(),
            });
        });

        const filter = this.appState.routerBackoffice.getParametersString('events', 'filterMarket');

        if (isMarketStateType(filter)) {
            this.filter = filter;
        } else {
            this.filter = 'open';
        }

        // Initialize openedMarket from URL parameter
        const marketId = this.appState.routerBackoffice.getParametersNumber('events', 'market');
        if (marketId !== null) {
            this.openedMarket = marketId;
        }

        makeAutoObservable(this);
    }

    @action public setFilter = (filter: string): void => {
        this.filter = isMarketStateType(filter) ? filter : 'open';
        this.appState.routerBackoffice.setParamInNamespace('events', 'filterMarket', filter);
        this.appState.routerBackoffice.removeParamInNamespace('events', 'sortOrder');
    };

    @action public onAddMarket = (): void => {
        this.add = true;
    };

    @action public onCloseAddMarket = (): void => {
        this.add = false;
    };

    @computed public get getAvailableMarkets(): Array<AvailableItemType> {
        const out: Array<AvailableItemType> = [];

        for (const item of AVAILABLE_MARKET_STATES) {
            out.push({
                id: item.id,
                name: item.label,
            });
        }

        return out;
    }

    private assignMarketToTab(market: EventMarketItemType): 'open' | 'new' | 'resulted' | 'settled' | 'unknown' {
        const status = getMarketStatus(market.id2);
        if (status === 'open' || status === 'suspended') {
            return 'open';
        }

        if (status === 'new') {
            return 'new';
        }

        if (status === 'resulted') {
            return 'resulted';
        }

        if (status === 'settled') {
            return 'settled';
        }

        console.error(`No mapped status to tab: ${status}`);

        return 'unknown';
    }

    @computed public get marketGroups(): GroupMarketsType | {} {
        const allEventMarkets = this.appState.sdkStaff.models.getEventMarketsAll(this.eventId.toOldId());
        if (allEventMarkets === null) {
            return {};
        }

        const marketId = this.appState.routerBackoffice.getParametersNumber('events', 'market');
        const groupedMarkets: GroupMarketsType = {};
        const marketsWithCurrentState = allEventMarkets.filter(
            (market) => this.assignMarketToTab(market) === this.filter
        );

        for (const market of marketsWithCurrentState) {
            const templateId = market.templateId;
            const expanded = marketId !== null && marketId === TypeId.getMarketId(market.id);
            const currentGroup = groupedMarkets[templateId];

            if (currentGroup === undefined) {
                groupedMarkets[templateId] = {
                    displayOrder: market.displayOrder,
                    id: TypeId.getMarketId(market.id),
                    expanded,
                    templateId,
                    items: [{ market, expanded, groupName: templateId }],
                };
            } else {
                currentGroup.id = TypeId.getMarketId(market.id);
                currentGroup.items.push({ market, expanded, groupName: templateId });
            }
        }

        return groupedMarkets;
    }

    @computed public get templateNamesById(): Map<string, string> {
        const templateNames = new Map<string, string>();
        const possibleMarkets = this.possibleMarkets.getReady() ?? [];

        for (const possibleMarket of possibleMarkets) {
            const internalId = possibleMarket.metadata?.['internal-id'];

            if (internalId !== undefined) {
                templateNames.set(internalId.toLowerCase(), possibleMarket.name);
            }
        }

        return templateNames;
    }

    @computed public get marketGroupsArray(): Array<MarketForRenderType> {
        return Object.values(this.marketGroups).sort((a, b) => a.displayOrder - b.displayOrder);
    }

    @action public newOnSort = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
        targetRect: ClientRect;
    }): Promise<void> => {
        const list = this.marketGroupsArray;
        const newList = arrayMove(list, oldIndex, newIndex);
        const indexedMarketList = reorderMarkets(newList);

        await this.appState.sdkStaff.event_reorder_markets({
            event_id: this.eventId.toOldId(),
            requestBody: {
                marketsOrder: indexedMarketList,
            },
        });
        await this.appState.eventStateActions.triggerUpdateEvent(this.eventId);
    };

    @action public toggle = (shortname: string, marketId: number): void => {
        const url = this.appState.routerBackoffice.getValueString('events');
        this.appState.routerBackoffice.removeParamInNamespace('events', 'sortOrder');
        if (marketId === this.openedMarket && url !== null) {
            // eslint-disable-next-line no-restricted-globals
            this.appState.routerBackoffice.closeEventMarketByFilter(Number(url), this.filter);
            this.openedMarket = null;
            this.opened = shortname;
            return;
        }

        const isMarketChanged = marketId !== this.openedMarket,
            isOpenedChanged = shortname !== this.opened,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            opened = isMarketChanged || (!isMarketChanged && !isNaN(marketId)) || isOpenedChanged ? shortname : null;

        this.opened = opened;
        this.openedMarket = opened !== null && (isOpenedChanged || isMarketChanged) ? marketId : null;
    };

    public setAllMarketsInPlay = async (): Promise<void> => {
        this.isConfirmPopupOpened = false;
        const allEventMarkets = this.appState.sdkStaff.models.getEventMarketsAll(this.eventId.toOldId());
        if (allEventMarkets === null) {
            return;
        }

        const marketModels: Array<MarketModel> = [];
        for (const market of allEventMarkets) {
            const marketModel = this.appState.sdkStaff.models.getMarket(market.id);
            if (marketModel !== null) {
                marketModels.push(marketModel);
            }
        }

        const marketData:
            | null
            | undefined
            | Array<{
                  betDelay: number;
                  marketId: string;
                  tradedInPlay: boolean;
              }> = [];

        for (const market of marketModels) {
            const id = await market.id2.idv2();
            marketData.push({
                marketId: id,
                betDelay: market.inPlayDelay,
                tradedInPlay: true,
            });
        }

        await this.appState.sdkStaff.updateEvent({
            event_id: this.eventId.toOldId(),
            requestBody: {
                markets: marketData,
            },
        });
    };

    public openConfirmPopup = (): void => {
        this.isConfirmPopupOpened = true;
    };

    public cancelChangeMarketsInPlay = (): void => {
        this.isConfirmPopupOpened = false;
    };
}
